<template>
<div>
  <div
    class="programs-item"
    :style="cardStyles"
  >
    <div>
      <div class="programs-item__pic">
        <img
          :src="program.logo"
          :alt="program.name"
        />
      </div>
      <h3 class="programs-item__topic">{{ program.name }}</h3>
      <div class="programs-item__description" v-html="program.description"></div>
    </div>
    <div>
      <ul class="programs-item__info">
        <li style="align-items: center;">
          <CalendarSVG />
          {{ program.date_start }} <br> {{ program.date_end }} 
        </li>
      </ul>
      <template v-if="role">
        <router-link
          v-if="program.applicationsByProgram"
          :to="{ name: 'application-create', params: { id: program.id },
          query: { app_id: program.applicationsByProgram.id, user_id: program.applicationsByProgram.user_id } }"
          class="btn btn--color-outline-bright"
        >
          <span>{{ program.applicationsByProgram.is_accepted ? registeredButtonText : continueApplicationButtonText }}</span>
        </router-link>
        <router-link
          v-else
          :disabled="isActiveRegistrationByRole(role, program) ||
            isDisabledRegistrationByGender(role, gender, program)"
          :to="getProgramRegistrationLinkByRole(role, program)"
          class="btn btn--color-outline-bright"
        ><span>{{ registerHereButtonText }}</span>
        </router-link>
        <button
          v-if="role === 'parent'"
          type="button"
          class="btn btn--color-outline-dark mt-3"
          @click="openAddChildModal"
        >
          <span class="capitalize">{{ addChildButtonText }}</span>
        </button>
      </template>
      <template v-else>
        <div>
          <router-link
            v-if="isParentRegistrationAvailable"
            :disabled="!program.is_active_for_camper"
            :to="{name: 'auth-login', params: { return: 'application-create', routeParams: { id: program.id }, role: 'parent' } }"
            class="btn btn--color-outline-bright"
          >
            <span class="capitalize">{{ camperRegistrationButtonText }}</span>
          </router-link>
          <router-link
            v-if="isStudentRegistrationAvailable"
            :disabled="!program.is_active_for_camper"
            :to="{name: 'auth-login', params: { return: 'application-create', routeParams: { id: program.id }, role: 'student' } }"
            class="btn btn--color-outline-bright"
          >
            <span class="capitalize">{{ camperRegistrationButtonText }}</span>
          </router-link>
          <router-link
            :disabled="!program.is_active_for_staff"
            :to="{name: 'auth-login', params: { return: 'application-create', routeParams: { id: program.id }, role: 'staff' } }"
            class="btn btn--color-outline-bright"
          >
            <span>{{ staffRegistrationButtonText }}</span>
          </router-link>
        </div>
      </template>
    </div>
  </div>

  <AddNewChildModal
    v-if="role === 'parent'"
    :is-show-modal="isShowAddChildModal"
    :default-gender="gender"
    :family-name="familyName"
    :family-id="familyId"
    @closeModal="closeAddChildModal"
  />
</div>

</template>

<script>
import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'

import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
import { getUserData } from '@/auth/utils'

import CalendarSVG from '@/assets/images/icons/calendar-2.svg'
import { mapGetters } from 'vuex'
import AddNewChildModal from '@/components/site/add-new-child-modal/AddNewChildModal.vue'

export default {
  components: {
    AddNewChildModal,
    CalendarSVG,
  },
  mixins: [prepareProgramRegistrationInfo, instanceSessionCamperNames],
  data: () => ({
    role: getUserData()?.role,
    id: getUserData()?.id,
    gender: getUserData()?.gender,
    fullName: getUserData()?.fullName,
    familyName: getUserData()?.last_name,
    familyId: getUserData()?.family_id,
    isShowAddChildModal: false,
  }),
  props: {
    program: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters({
      authSettings: 'authSettings/getAuthSettings',
    }),
    isParentRegistrationAvailable() {
      return this.availableRegularRegistration('parent', this.authSettings) || this.availableOauthRegistration('parent', this.authSettings)
    },
    isStudentRegistrationAvailable() {
      return this.availableRegularRegistration('student', this.authSettings) || this.availableOauthRegistration('student', this.authSettings)
    },
    availableCamperRegistration() {
      if (this.isStudentRegistrationAvailable) {
        return this.program.is_active_for_camper && this.program.applicable_role.includes(this.role)
      }
      return this.program.is_active_for_camper
    },
    cardStyles() {
      if (this.program.styling.use_custom_card_styles) {
        return {
          backgroundColor: this.program.styling.background_color,
          borderColor: this.program.styling.border_color,
        }
      }
      return {}
    },
    registeredButtonText() {
      return this.getCustomText(this.authSettings, 'program_card_registered_button', 'Registered')
    },
    continueApplicationButtonText() {
      return this.getCustomText(this.authSettings, 'program_card_continue_application_button', 'Continue application')
    },
    registerHereButtonText() {
      return this.getCustomText(this.authSettings, 'program_card_register_here_button', 'Register Here')
    },
    addChildButtonText() {
      return this.getCustomText(
        this.authSettings,
        'application_select_child_add_button',
        'Add A {camperStudent}',
        { camperStudent: this.camperStudent('singular') },
      )
    },
    camperRegistrationButtonText() {
      return this.getCustomText(
        this.authSettings,
        'program_card_camper_registration_button',
        '{camperStudent} Registration',
        { camperStudent: this.camperStudent('singular') },
      )
    },
    staffRegistrationButtonText() {
      return this.getCustomText(this.authSettings, 'program_card_staff_registration_button', 'Staff Registration')
    },
  },
  methods: {
    openAddChildModal() {
      this.isShowAddChildModal = true
    },
    closeAddChildModal() {
      this.isShowAddChildModal = false
    },
  },
}
</script>

<style scoped>

</style>
